import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import type { ApiEnvelope } from '@etherworks/shared-types';
import {
  Receipt,
  CreditCard,
  DollarSign,
  AlertTriangle,
  CheckCircle2,
  Clock,
  XCircle,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { formatDateTimeShort } from '../../utils/dateFormat';

interface UserPayment {
  _id: string;
  amount: number;
  currency: string;
  status: 'pending' | 'completed' | 'failed' | 'cancelled';
  description?: string;
  createdAt: string;
  metadata?: {
    packagePrice?: number;
    context?: {
      type?: string;
    };
    paidWithBalance?: boolean;
  };
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

import PageTitle from '../../components/PageTitle';

function getCurrencySymbol(currency: string) {
  const upper = currency.toUpperCase();
  return upper === 'USD' ? '$' : `${upper} `;
}

function getPaymentDirection(payment: UserPayment) {
  if (payment.metadata?.context?.type === 'topup') {
    return 'credit';
  }

  if (
    payment.metadata?.context?.type === 'subscription'
    || payment.metadata?.context?.type === 'subscription_shortfall'
    || payment.metadata?.paidWithBalance
  ) {
    return 'debit';
  }

  return 'neutral';
}

function formatPaymentAmount(payment: UserPayment) {
  const direction = getPaymentDirection(payment);
  const sign = direction === 'credit' ? '+' : direction === 'debit' ? '-' : '';
  return `${sign}${getCurrencySymbol(payment.currency)}${payment.amount.toFixed(2)}`;
}

export default function BillingHistory() {
  const { t } = useTranslation(['settings', 'common']);
  const [payments, setPayments] = useState<UserPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });

  const loadData = async (page = pagination.page) => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString(),
      });
      const response = await axios.get<ApiEnvelope<{ payments: UserPayment[]; pagination: PaginationInfo }>>('/api/billing/history?' + params);
      if (response.data.code !== 0 || !response.data.data) {
        throw new Error(response.data.msg || t('billing.load_error'));
      }
      setPayments(response.data.data.payments);
      setPagination(response.data.data.pagination);
    } catch (err: any) {
      console.error('Failed to load payment history:', err);
      setError(err.response?.data?.error || t('billing.load_error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [pagination.page]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.pages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  /* ── Computed summary stats ──────────────────────────────────── */
  const stats = useMemo(() => {
    const total = pagination.total;
    const completed = payments.filter(p => p.status === 'completed').length;
    const pending = payments.filter(p => p.status === 'pending').length;
    const spentPayments = payments.filter(p => p.status === 'completed' && getPaymentDirection(p) === 'debit');
    const totalSpent = spentPayments
      .reduce((s, p) => s + p.amount, 0);
    const spentCurrencies = new Set(spentPayments.map(p => p.currency.toUpperCase()));
    const totalSpentDisplay = spentCurrencies.size === 1
      ? `-${getCurrencySymbol(spentPayments[0].currency)}${totalSpent.toFixed(2)}`
      : `-${totalSpent.toFixed(2)}`;

    return { total, completed, pending, totalSpentDisplay };
  }, [payments, pagination.total]);

  /* ── Status styling helper ──────────────────────────────────── */
  const statusConfig = (status: string) => {
    switch (status) {
      case 'completed':
        return {
          bg: 'bg-emerald-500/10',
          text: 'text-emerald-600 dark:text-emerald-400',
          icon: <CheckCircle2 className="h-3.5 w-3.5" />,
          label: t('billing.completed'),
        };
      case 'pending':
        return {
          bg: 'bg-amber-500/10',
          text: 'text-amber-600 dark:text-amber-400',
          icon: <Clock className="h-3.5 w-3.5" />,
          label: t('billing.pending'),
        };
      case 'failed':
        return {
          bg: 'bg-destructive/10',
          text: 'text-destructive',
          icon: <XCircle className="h-3.5 w-3.5" />,
          label: t('billing.failed'),
        };
      case 'cancelled':
        return {
          bg: 'bg-muted',
          text: 'text-muted-foreground',
          icon: <XCircle className="h-3.5 w-3.5" />,
          label: t('billing.cancelled'),
        };
      default:
        return {
          bg: 'bg-muted',
          text: 'text-muted-foreground',
          icon: null,
          label: t(`billing.${status}`, status),
        };
    }
  };

  /* ── Loading state ──────────────────────────────────────────── */
  if (loading && payments.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  /* ── Error state ────────────────────────────────────────────── */
  if (error) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 max-w-md w-full text-center">
          <div className="w-12 h-12 bg-destructive/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="h-6 w-6 text-destructive" />
          </div>
          <h3 className="text-sm font-semibold text-foreground mb-1">{t('billing.error_loading')}</h3>
          <p className="text-xs text-muted-foreground mb-4">{error}</p>
          <button
            onClick={() => loadData(1)}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-xl text-xs font-medium hover:bg-primary/90 transition-colors"
          >
            {t('common:retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col gap-4 max-w-full mx-auto">
      <PageTitle 
        titleKey="billing.title" 
        namespace="settings" 
        descriptionKey="billing.subtitle"
      />
      {/* Section Header */}
      <div className="shrink-0">
        <h2 className="text-lg font-semibold text-foreground tracking-tight">{t('billing.title')}</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {t('billing.subtitle')}
        </p>
      </div>

      {/* Summary Stats */}
      <div className="shrink-0 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Payments */}
        <div className="bg-card rounded-lg border border-border shadow-xs p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
            <Receipt className="h-4.5 w-4.5 text-primary" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">{t('billing.payments')}</p>
            <p className="text-lg font-bold text-foreground truncate">{stats.total}</p>
          </div>
        </div>

        {/* Total Spent */}
        <div className="bg-card rounded-lg border border-border shadow-xs p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
            <DollarSign className="h-4.5 w-4.5 text-emerald-500" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">{t('billing.total_spent')}</p>
            <p className="text-lg font-bold text-foreground truncate">{stats.totalSpentDisplay}</p>
          </div>
        </div>

        {/* Completed */}
        <div className="bg-card rounded-lg border border-border shadow-xs p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
            <CheckCircle2 className="h-4.5 w-4.5 text-emerald-500" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">{t('billing.completed')}</p>
            <p className="text-lg font-bold text-foreground truncate">{stats.completed}</p>
          </div>
        </div>

        {/* Pending */}
        <div className="bg-card rounded-lg border border-border shadow-xs p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0">
            <Clock className="h-4.5 w-4.5 text-amber-500" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">{t('billing.pending')}</p>
            <p className="text-lg font-bold text-foreground truncate">{stats.pending}</p>
          </div>
        </div>
      </div>

      {/* Transaction List */}
      <div className="flex-1 min-h-0 bg-card rounded-lg border border-border shadow-xs flex flex-col overflow-hidden">
        {/* List Header */}
        <div className="shrink-0 px-5 py-3 border-b border-border bg-muted/20 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-muted-foreground" />
            {t('billing.transactions')}
          </h3>
          {pagination.total > 0 && (
            <span className="text-xs text-muted-foreground">
              {t('common:showing_range', { start: ((pagination.page - 1) * pagination.limit) + 1, end: Math.min(pagination.page * pagination.limit, pagination.total), total: pagination.total })}
            </span>
          )}
        </div>

        {/* List Body */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {payments.length === 0 ? (
            /* Empty State */
            <div className="flex flex-col items-center justify-center h-full py-16 px-6">
              <div className="w-14 h-14 bg-muted rounded-lg flex items-center justify-center mb-4 border-2 border-dashed border-border">
                <CreditCard className="h-7 w-7 text-muted-foreground" />
              </div>
              <h3 className="text-sm font-semibold text-foreground mb-1">{t('billing.no_payments')}</h3>
              <p className="text-xs text-muted-foreground text-center max-w-xs">
                {t('billing.no_payments_desc')}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {payments.map(payment => {
                const sc = statusConfig(payment.status);
                return (
                  <div
                    key={payment._id}
                    className="px-5 py-3.5 flex items-center gap-4 hover:bg-muted/30 transition-colors group"
                  >
                    {/* Status Icon */}
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${sc.bg} ${sc.text}`}>
                      {sc.icon}
                    </div>

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-foreground truncate">
                          {payment.description || t('billing.payment')}
                        </p>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${sc.bg} ${sc.text}`}>
                          {sc.label}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {formatDateTimeShort(payment.createdAt)}
                      </p>
                    </div>

                    {/* Package Price */}
                    {payment.metadata?.packagePrice != null && (
                      <div className="hidden sm:block text-right shrink-0">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{t('billing.package')}</p>
                        <p className="text-xs font-medium text-foreground">${payment.metadata.packagePrice.toFixed(2)}</p>
                      </div>
                    )}

                    {/* Amount */}
                    <div className="text-right shrink-0 min-w-[80px]">
                      <p className={`text-sm font-bold ${getPaymentDirection(payment) === 'credit' ? 'text-emerald-600' : getPaymentDirection(payment) === 'debit' ? 'text-foreground' : 'text-foreground'}`}>
                        {formatPaymentAmount(payment)}
                      </p>
                      <p className="text-[10px] text-muted-foreground uppercase">
                        {payment.currency.toUpperCase()}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Pagination Footer */}
        {pagination.pages > 1 && (
          <div className="shrink-0 px-5 py-3 border-t border-border bg-muted/20 flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              {t('common:page_of', { current: pagination.page, total: pagination.pages })}
            </p>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="p-1.5 rounded-lg bg-muted text-foreground hover:bg-muted/80 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>

              {/* Page numbers */}
              {Array.from({ length: Math.min(pagination.pages, 5) }, (_, i) => {
                let pageNum: number;
                if (pagination.pages <= 5) {
                  pageNum = i + 1;
                } else if (pagination.page <= 3) {
                  pageNum = i + 1;
                } else if (pagination.page >= pagination.pages - 2) {
                  pageNum = pagination.pages - 4 + i;
                } else {
                  pageNum = pagination.page - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`w-7 h-7 rounded-lg text-xs font-medium transition-colors ${
                      pagination.page === pageNum
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}

              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.pages}
                className="p-1.5 rounded-lg bg-muted text-foreground hover:bg-muted/80 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
